import React, { useState, useEffect } from 'react';
import { LoanType } from '../../../common/LoanConstants';
import {
    InsuranceType,
    InsuranceTypeOptions,
    TerminationTypeOptions,
    InsuranceFileType,
    InsuranceOverrideType,
    InsuranceOverrideTypeOptions,
} from '../InsuranceConstants';
import { getNewInsurance, isInsuranceRequired } from '../InsuranceFunctions';
import DatePicker from 'react-datepicker';
import FileOperationsModal from '../modals/FileOperationsModal';
import CancelInsuranceModal from '../modals/CancelInsuranceModal';
import HourglassIcon from '@material-ui/icons/HourglassEmptyOutlined';
import store, { loanActions } from '@apply/adminStore';
import { formatDate } from '../../../../common/SharedFunctions';
import { FileApi } from '@apply/api';
import { applicationInsights } from '../../../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { v4 as uuidv4 } from 'uuid';
import { NumericFormat } from 'react-number-format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';

export const InsuranceGrid = ({ insuranceGroup, committedAmount, onChange, enabled }) => {
    const { dispatch } = store;
    const fileApi = new FileApi.FileApi(applicationInsights);

    const styles = {
        noPointer: { cursor: 'default' },
        iconYes: { color: 'mediumblue', cursor: 'pointer' },
        iconYesCancel: { color: '#721601', cursor: 'pointer' },
        iconNo: { color: 'silver', cursor: 'default' },
        iconCancel: { color: 'darkred', cursor: 'pointer' },
        iconRed: { color: 'darkred' },
        error: { backgroundColor: '#FEEFEC', cursor: 'default' },
    };

    //File Dialogs
    const [fileOpsOptions, set_fileOpsOptions] = useState({
        isOpen: false,
        files: [],
    });

    //Load Panel
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    useEffect(() => {
        if (!insuranceGroup) {
            return;
        }

        const isOk = checkAllGood();
        if (!isOk) {
            insuranceGroup.allGood = isOk;
        }
    }, [insuranceGroup]);

    const updateParent = (amountChanged) => {
        if (amountChanged) {
            const isOk = checkAllGood();
            if (!isOk) {
                insuranceGroup.allGood = false;
            } else {
                insuranceGroup.allGood = !insuranceGroup.notes || insuranceGroup.notes.length === 0;
            }
        }
        onChange && onChange({ ...insuranceGroup });
    };

    const onRemove = (index) => {
        if (!insuranceGroup) {
            return;
        }

        insuranceGroup.insurances.splice(index, 1);
        updateParent();
    };

    const onAdd = () => {
        if (!insuranceGroup) {
            return;
        }

        const newProp = getNewInsurance();
        insuranceGroup.insurances.push(newProp);
        updateParent();
    };

    const setAmount1 = (index, val) => {
        if (!insuranceGroup) {
            return;
        }

        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }

        if (insurance.insuranceTypeId === InsuranceType.WorkmansComp) {
            if (isNaN(val) || val < 0) {
                insurance.amount1 = '';
            } else {
                insurance.amount1 = val;
            }
        } else {
            if (isNaN(val) || val <= 0) {
                insurance.amount1 = '';
            } else {
                insurance.amount1 = val;
            }
        }
        updateParent(true);
    };

    const setAmount2 = (index, val) => {
        if (!insuranceGroup) {
            return;
        }
        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }
        if (!val || isNaN(val) || val <= 0) {
            insurance.amount2 = '';
        } else {
            insurance.amount2 = val;
        }
        updateParent(true);
    };

    const setName = (index, value) => {
        if (!insuranceGroup) {
            return;
        }

        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }
        insurance.name = value;
        updateParent();
    };

    const setExpirationDate = (index, value) => {
        if (!insuranceGroup) {
            return;
        }

        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }
        insurance.expirationDate = value.toLocaleDateString('en-us');
        updateParent();
    };

    const setBfiNamed = (index, value) => {
        if (!insuranceGroup) {
            return;
        }

        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }
        insurance.bfiNamed = !!value;
        updateParent();
    };

    const onCancelSubmit = async (guid, cancelDate, files) => {
        dispatch(
            loanActions.setCancelInsuranceModal({
                cancelInsuranceModal: {
                    isOpen: false,
                },
            })
        );

        //Find the insurance
        const ins = insuranceGroup.insurances.find((i) => i.guid === guid);
        if (!ins) {
            return;
        }

        ins.cancelled = true;
        ins.cancelDate = cancelDate;
        await uploadFiles(files, ins, InsuranceFileType.Cancellation);
        updateParent();
    };

    const setCancelled = (index, isCancelled) => {
        if (!insuranceGroup) {
            return;
        }

        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }

        if (isCancelled) {
            dispatch(
                loanActions.setCancelInsuranceModal({
                    cancelInsuranceModal: {
                        isOpen: true,
                        id: insurance.guid,
                        insuranceTypeId: insurance.insuranceTypeId,
                        onSubmit: onCancelSubmit,
                    },
                })
            );
        } else {
            insurance.cancelled = false;
            insurance.cancelDate = null;
            insurance.cancelFiles = [];
            updateParent();
        }
    };

    const set_forcePlaced = (index, isForcePlaced) => {
        if (!insuranceGroup) {
            return;
        }

        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }

        insurance.forcePlaced = isForcePlaced;

        // Update forcePlacedDate based on toggle
        insurance.forcePlacedDate = isForcePlaced
            ? new Date().toLocaleDateString('en-us') // when ON
            : null; // when OFF

        updateParent();
    };

    const setTerminationType = (index, value) => {
        if (!insuranceGroup) {
            return;
        }

        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }
        insurance.terminationTypeId = value;
        updateParent();
    };

    const setChecksOverrideTypeId = (index, value) => {
        if (!insuranceGroup) {
            return;
        }

        const insurance = insuranceGroup.insurances[index];
        if (!insurance) {
            return;
        }
        insurance.checksOverrideTypeId = value;
        updateParent(true);
    };

    const setAllGood = (value) => {
        if (!insuranceGroup) {
            return;
        }

        insuranceGroup.allGood = value === '1';
        insuranceGroup.oldAllGood = insuranceGroup.allGood;
        updateParent();
    };

    const setNotes = (value) => {
        if (!insuranceGroup) {
            return;
        }

        insuranceGroup.notes = value;
        updateParent();
    };

    const uploadFiles = async (files, insurance, fileTypeId) => {
        if (!files || files.length === 0) {
            return;
        }

        try {
            set_loadingMessage(`Uploading files to temporary storage...`);
            set_loadingOpen(true);

            const upLoadResult = await fileApi.uploadTempFiles(files);
            const result = upLoadResult.result;
            result.forEach((f) => {
                const newFile = {
                    tempFileName: f.tempName,
                    fileName: f.fileName,
                    fileTypeId,
                    url: f.url,
                };
                if (!insurance.files) {
                    insurance.files = [];
                }
                insurance.files.push(newFile);
            });
        } finally {
            set_loadingOpen(false);
        }
    };

    const onFileOpsSubmit = async (id, files) => {
        set_fileOpsOptions({ ...fileOpsOptions, isOpen: false });

        if (!id || !insuranceGroup || !files) {
            return;
        }

        const insurance = insuranceGroup.insurances.find((f) => f.guid === id);
        if (!insurance) {
            return;
        }

        //get all new files and upload those
        const newFiles = files.filter((f) => f.file && !f.deleted).map((f) => f.file);
        if (newFiles.length > 0) {
            await uploadFiles(newFiles, insurance, InsuranceFileType.Policy);
        }

        const otherFiles = files.filter((f) => !f.file);
        otherFiles.forEach((f) => {
            const target = insurance.files.find((a) => a.guid === f.guid);
            if (target) {
                target.deleted = f.deleted;
            }
        });

        updateParent();
    };

    const onFileOpsCancel = () => {
        set_fileOpsOptions({ ...fileOpsOptions, isOpen: false });
    };

    const showFileOptions = (event, insurance) => {
        event.preventDefault();
        event.stopPropagation();

        insurance.files = insurance?.files ?? [];

        insurance.files.forEach((f) => {
            if (!f.guid) {
                f.guid = uuidv4();
            }
            if (!f.typeName) {
                f.typeName = f.fileTypeId === InsuranceFileType.Policy ? 'Policy' : 'Cancellation';
            }
        });

        set_fileOpsOptions({
            insuranceTypeName: insurance.name ?? InsuranceTypeOptions.find((elm) => elm.id === insurance.insuranceTypeId)?.name,
            isOpen: true,
            parentId: insurance.guid,
            files: [...insurance.files],
        });
    };

    const hasLiability = () => {
        if (!insuranceGroup) {
            return false;
        }
        var gl = insuranceGroup.insurances.find((i) => i.insuranceTypeId === InsuranceType.GeneralLiability);
        return !!gl;
    };

    const allGoodTypes = [
        { id: 1, name: 'Yes' },
        { id: 0, name: 'No' },
    ];

    const toInteger = (val) => {
        if (!val) {
            return 0;
        }

        const intVal = parseInt(val);
        if (!intVal) {
            return 0;
        }

        return intVal;
    };

    const clearError = (msg) => {
        if (!insuranceGroup) {
            return;
        }

        if (insuranceGroup.notes && insuranceGroup.notes.includes(msg)) {
            insuranceGroup.notes = insuranceGroup.notes.replace(msg, '');
        }
    };

    const setError = (msg, insurance) => {
        if (!insuranceGroup.notes) {
            insuranceGroup.notes = '';
        }
        if (!insuranceGroup.notes.includes(msg)) {
            insurance.error = msg;
            insuranceGroup.notes = msg + insuranceGroup.notes;
        }
    };

    const checkAllGood = () => {
        //Calculate if there are any issues
        if (
            !insuranceGroup ||
            !insuranceGroup.loanTypeId ||
            !insuranceGroup.insurances ||
            !insuranceGroup.insurances.length === 0
        ) {
            return true;
        }

        let isOk = true;
        const glError = '*Liability is less than $1 million instance / $2 million aggregate*\n';
        clearError(glError);
        let isRequired = isInsuranceRequired(insuranceGroup.loanTypeId, InsuranceType.GeneralLiability);
        if (isRequired) {
            const glIns = insuranceGroup.insurances.find((i) => i.insuranceTypeId === InsuranceType.GeneralLiability);
            if (glIns) {
                glIns.error = null;
                if (!glIns.cancelled) {
                    if (
                        (glIns.amount1 && toInteger(glIns.amount1) < 1000000) ||
                        (glIns.amount2 && toInteger(glIns.amount2) < 2000000)
                    ) {
                        if (glIns.checksOverrideTypeId === InsuranceOverrideType.None) {
                            setError(glError, glIns);
                            isOk = false;
                        }
                    }
                }
            }
        }

        const brError = "*Builder's Risk is less than 125% of the commitment amount*\n";
        clearError(brError);
        isRequired = isInsuranceRequired(insuranceGroup.loanTypeId, InsuranceType.BuildersRisk);
        if (isRequired) {
            const brIns = insuranceGroup.insurances.find((i) => i.insuranceTypeId === InsuranceType.BuildersRisk);
            if (brIns) {
                brIns.error = null;
                if (!brIns.cancelled) {
                    const commited = toInteger(committedAmount);
                    if (commited > 0 && brIns.amount1) {
                        if (toInteger(brIns.amount1) < Math.floor(1.25 * commited)) {
                            if (brIns.checksOverrideTypeId === InsuranceOverrideType.None) {
                                setError(brError, brIns);
                                isOk = false;
                            }
                        }
                    }
                }
            }
        }

        const flError = '*Flood is less than $250,000 AND less than 102% of Approved Amount + $500*\n';
        clearError(flError);
        isRequired = insuranceGroup.inFloodZone || isInsuranceRequired(insuranceGroup.loanTypeId, InsuranceType.Flood);
        if (isRequired) {
            const flIns = insuranceGroup.insurances.find((i) => i.insuranceTypeId === InsuranceType.Flood);
            if (flIns) {
                flIns.error = null;
                if (!flIns.cancelled) {
                    const adjustedAmount = toInteger(committedAmount) * 1.02 + 500;

                    if (adjustedAmount > 0 && flIns.amount1) {
                        if (toInteger(flIns.amount1) < 250000 && toInteger(flIns.amount1) < adjustedAmount) {
                            if (flIns.checksOverrideTypeId === InsuranceOverrideType.None) {
                                setError(flError, flIns);
                                isOk = false;
                            }
                        }
                    }
                }
            }
        }

        const dwError = '*Dwelling is less than the commitment amount*\n';
        clearError(dwError);
        isRequired = isInsuranceRequired(insuranceGroup.loanTypeId, InsuranceType.Dwelling);
        if (isRequired) {
            const dwIns = insuranceGroup.insurances.find((i) => i.insuranceTypeId === InsuranceType.Dwelling);
            if (dwIns) {
                dwIns.error = null;
                if (!dwIns.cancelled) {
                    const commited = toInteger(committedAmount);
                    if (commited > 0 && dwIns.amount1) {
                        if (toInteger(dwIns.amount1) < commited) {
                            if (dwIns.checksOverrideTypeId === InsuranceOverrideType.None) {
                                setError(dwError, dwIns);
                                isOk = false;
                            }
                        }
                    }
                }
            }
        }

        const wcError = "*Workman's Comp is 0*\n";
        clearError(wcError);
        isRequired = isInsuranceRequired(insuranceGroup.loanTypeId, InsuranceType.WorkmansComp);
        if (isRequired) {
            const wcIns = insuranceGroup.insurances.find((i) => i.insuranceTypeId === InsuranceType.WorkmansComp);
            if (wcIns) {
                wcIns.error = null;
                if (!wcIns.cancelled) {
                    if (
                        wcIns.amount1 !== undefined &&
                        wcIns.amount1 !== null &&
                        wcIns.amount1 !== '' &&
                        toInteger(wcIns.amount1) === 0
                    ) {
                        if (wcIns.checksOverrideTypeId === InsuranceOverrideType.None) {
                            setError(wcError, wcIns);
                            isOk = false;
                        }
                    }
                }
            }
        }

        insuranceGroup.allGoodDisabled = !isOk;
        return isOk;
    };

    return (
        <>
            <div className='insurance-grid'>
                <table>
                    <thead>
                        <tr style={styles.noPointer}>
                            <td>&nbsp;</td>
                            <td colSpan={hasLiability() ? '3' : '1'}>Coverage</td>
                            <td colSpan='2'>Expiration</td>
                            <td>
                                {insuranceGroup?.loanTypeId === LoanType.MasterNote ||
                                insuranceGroup?.loanTypeId === LoanType.SubNoteConstruction ||
                                insuranceGroup?.loanTypeId === LoanType.SubNoteFixFlip
                                    ? '1FB Nmd'
                                    : 'BFI Nmd'}
                            </td>
                            <td colSpan='2'>Files</td>
                            <td>Cancelled</td>
                            <td>Force Placed?</td>
                            <td>Override Checks</td>
                            <td>&nbsp;</td>
                        </tr>
                    </thead>
                    <tbody>
                        {insuranceGroup?.insurances.map((insurance, index) => (
                            <>
                                <tr
                                    key={'ig_' + insurance.insuranceTypeId}
                                    style={insurance.error ? styles.error : styles.noPointer}
                                >
                                    <td
                                        style={{
                                            fontWeight: 'bold',
                                            whiteSpace: ' nowrap',
                                            width: '140px',
                                        }}
                                    >
                                        {insurance.insuranceTypeId === InsuranceType.Other && (
                                            <input
                                                type='text'
                                                id={'name' + index}
                                                maxLength='20'
                                                required={true}
                                                placeholder='Coverage name...'
                                                value={insurance.name}
                                                onChange={(event) => setName(index, event.target.value)}
                                                disabled={insurance.cancelled}
                                            />
                                        )}
                                        {insurance.insuranceTypeId !== InsuranceType.Other && (
                                            <>
                                                {insurance.error && (
                                                    <span
                                                        className='reddot'
                                                        style={{ left: '5px' }}
                                                        title={insurance.error ?? ''}
                                                    ></span>
                                                )}
                                                <span
                                                    style={{
                                                        paddingLeft: '12px',
                                                    }}
                                                >
                                                    {
                                                        InsuranceTypeOptions.find((elm) => elm.id === insurance.insuranceTypeId)
                                                            ?.name
                                                    }
                                                </span>
                                            </>
                                        )}
                                    </td>
                                    <td style={{ width: '120px' }}>
                                        <NumericFormat
                                            thousandSeparator=','
                                            prefix='$'
                                            id={'amount1' + index}
                                            name={'amount1' + index}
                                            placeholder={
                                                insurance.insuranceTypeId === InsuranceType.GeneralLiability
                                                    ? 'Instance...'
                                                    : 'Amount...'
                                            }
                                            value={insurance.amount1 ?? ''}
                                            decimalScale={2}
                                            allowNegative={false}
                                            onValueChange={(values) => {
                                                setAmount1(index, values.floatValue);
                                            }}
                                            required={false}
                                            // isAllowed={(values, sourceInfo) => {
                                            //   const value = values?.floatValue;
                                            //   return value === undefined || value === null || value >= 0;
                                            // }}
                                            disabled={!enabled || insurance.cancelled}
                                        />
                                    </td>
                                    {hasLiability() && (
                                        <>
                                            <td style={{ width: '10px' }}>
                                                {insurance.insuranceTypeId === InsuranceType.GeneralLiability && <>/</>}
                                            </td>
                                            <td style={{ width: '120px' }}>
                                                {insurance.insuranceTypeId === InsuranceType.GeneralLiability && (
                                                    <NumericFormat
                                                        thousandSeparator=','
                                                        prefix='$'
                                                        id={'amount2' + index}
                                                        name={'amount2' + index}
                                                        placeholder='Aggregate...'
                                                        value={insurance.amount2 ?? ''}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        onValueChange={(values) => {
                                                            setAmount2(index, values.floatValue);
                                                        }}
                                                        required={false}
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                        disabled={!enabled || insurance.cancelled}
                                                    />
                                                )}
                                            </td>
                                        </>
                                    )}
                                    <td style={{ width: '90px' }}>
                                        {!insurance.cancelled && (
                                            <select
                                                id={'terminationTypeId' + index}
                                                value={insurance.terminationTypeId ?? 0}
                                                onChange={(event) => setTerminationType(index, parseInt(event.target.value))}
                                            >
                                                {TerminationTypeOptions.map((fs) => {
                                                    return (
                                                        <option
                                                            key={'tt_' + fs.id}
                                                            value={fs.id}
                                                        >
                                                            {fs.name}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        )}
                                        {insurance.cancelled && <>Cancelled: </>}
                                    </td>
                                    <td style={{ width: '120px' }}>
                                        {(insurance.terminationTypeId ?? 0) === 0 && !insurance.cancelled && (
                                            <DatePicker
                                                id={'orderedDate' + index}
                                                selected={insurance.expirationDate ? new Date(insurance.expirationDate) : null}
                                                onChange={(event) => setExpirationDate(index, event)}
                                                disabled={!enabled}
                                                popperPlacement='bottom'
                                            />
                                        )}
                                        {insurance.terminationTypeId === 1 && !insurance.cancelled && (
                                            <span>Cont. until Term.</span>
                                        )}
                                        {insurance.cancelled && <span>{formatDate(insurance.cancelDate)}</span>}
                                    </td>
                                    <td
                                        style={{
                                            textAlign: 'center',
                                            width: '70px',
                                        }}
                                    >
                                        <input
                                            type='checkbox'
                                            checked={insurance.bfiNamed}
                                            id={'bfiNamed' + index}
                                            disabled={!enabled || insurance.cancelled}
                                            onChange={(event) => setBfiNamed(index, event.target.checked)}
                                        />
                                    </td>
                                    <td style={{ width: '90px' }}>
                                        <button
                                            className='gridButton'
                                            title='View, Add, Download & Delete Files'
                                            onClick={(e) => showFileOptions(e, insurance)}
                                        >
                                            ...
                                        </button>
                                        &nbsp;&nbsp;
                                        {insurance?.files && (
                                            <span>
                                                {insurance.files.length}&nbsp;
                                                {insurance.files.length === 1 ? 'File' : 'Files'}
                                            </span>
                                        )}
                                    </td>
                                    <td
                                        style={{
                                            width: '20px',
                                            paddingTop: '10px',
                                        }}
                                    >
                                        {insurance?.files?.some((f) => f.deleted || f.tempFileName) && (
                                            <span title='File changes pending Save'>
                                                <HourglassIcon style={styles.iconRed} />
                                            </span>
                                        )}
                                    </td>
                                    <td
                                        style={{
                                            textAlign: 'center',
                                            width: '80px',
                                        }}
                                    >
                                        <input
                                            type='checkbox'
                                            checked={insurance.cancelled}
                                            id={'cancelled' + index}
                                            onChange={(event) => setCancelled(index, !insurance.cancelled)}
                                            disabled={!insurance.id}
                                        />
                                    </td>
                                    <td
                                        style={{
                                            textAlign: 'center',
                                            width: '160px',
                                        }}
                                    >
                                        <input
                                            type='checkbox'
                                            checked={insurance.forcePlaced}
                                            id={'forcePlaced' + index}
                                            onChange={(event) => set_forcePlaced(index, !insurance.forcePlaced)}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            style={{ width: '160px' }}
                                            id={'checksOverrideTypeId' + index}
                                            value={insurance.checksOverrideTypeId ?? 0}
                                            onChange={(event) => setChecksOverrideTypeId(index, parseInt(event.target.value))}
                                        >
                                            {InsuranceOverrideTypeOptions.map((fs) => {
                                                return (
                                                    <option
                                                        key={'io_' + fs.id}
                                                        value={fs.id}
                                                    >
                                                        {fs.name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </td>
                                    <td style={{ paddingTop: '15px' }}>
                                        {insurance.insuranceTypeId === 5 && (
                                            <button
                                                type='button'
                                                style={{ marginTop: '5px' }}
                                                className='button primary'
                                                onClick={() => onRemove && onRemove(index)}
                                                title='Delete this coverage'
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                {insurance.error && (
                                    <tr style={insurance.error ? styles.error : styles.noPointer}>
                                        <td></td>
                                        <td colSpan='11'>
                                            <span
                                                style={{
                                                    color: 'red',
                                                    fontSize: 'x-small',
                                                }}
                                            >
                                                Insufficient Coverage
                                            </span>
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))}
                    </tbody>
                </table>
                <div style={{ paddingTop: '10px' }}>
                    <button
                        type='button'
                        className='button primary'
                        title='Add a new coverage'
                        onClick={() => onAdd && onAdd()}
                        disabled={!enabled}
                    >
                        <FontAwesomeIcon icon={faPlus} /> Add Coverage
                    </button>
                </div>
            </div>
            <div className='row'>
                <div
                    className='column small-12 large-4'
                    style={styles.formColumn}
                >
                    <label htmlFor='allGood'>
                        Otherwise all good?<span className='req'>*</span>
                    </label>
                    <select
                        id='allGood'
                        value={insuranceGroup?.allGood ? '1' : '0'}
                        onChange={(event) => setAllGood(event.target.value)}
                        required={true}
                        disabled={insuranceGroup.allGoodDisabled ?? true}
                    >
                        {allGoodTypes.map((fs) => {
                            return (
                                <option
                                    key={'ag_' + fs.id}
                                    value={fs.id}
                                >
                                    {fs.name}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>
            <div className='row'>
                <div
                    className='column small-12 large-12'
                    style={styles.formColumn}
                >
                    <label htmlFor='notes'>
                        Notes
                        {!(insuranceGroup?.allGood ?? false) ? <span className='req'>*</span> : ''}
                    </label>
                    <textarea
                        id='notes'
                        value={insuranceGroup?.notes ?? ''}
                        required={!(insuranceGroup?.allGood ?? false)}
                        onChange={(event) => setNotes(event.target.value)}
                        rows={3}
                        maxLength={500}
                    />
                </div>
            </div>
            <CancelInsuranceModal />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <FileOperationsModal
                options={fileOpsOptions}
                onSubmit={onFileOpsSubmit}
                onCancel={onFileOpsCancel}
            />
        </>
    );
};
